import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabaseClient';
import { NotificationContainer } from '../../components/ui/NotificationToast';
import AssetDetailsSection from './components/AssetDetailsSection';
import FinancialSection from './components/FinancialSection';
import TechnicalSpecsSection from './components/TechnicalSpecsSection';
import ImageUpload from './components/ImageUpload';
import Button from '../../components/ui/Button';
import { useSelector } from 'react-redux'; // Import useSelector
import { formatDate } from '../../utils/formatters';
import { usePreferences } from '../../hooks/usePreferences';
import { logActivity } from '../../utils/activityLogger'; // Import logActivity
import BulkImport from './components/BulkImport'; // Import BulkImport

// --- 1. Zod Validation Schema ---
const assetRegistrationSchema = z.object({
  model: z.string().min(3, { message: 'Asset name must be at least 3 characters long' }),
  category: z.string().min(1, "Category is required"),
  serial_number: z.string().regex(/^[a-zA-Z0-9_\-\/\\]+$/, { message: 'Serial number must be alphanumeric (symbols like -, _, /, and \\ allowed)' }),
  product_name: z.string().optional(),
  purchase_date: z.coerce.date(),
  purchase_price: z.coerce.number().positive({ message: 'Price must be a positive number' }),
  warranty_months: z.coerce.number().int().min(0, { message: 'Warranty must be a positive number' }),
  supplier_id: z.string().min(1, "Supplier is required"),
  location_id: z.string().optional().nullable(),
  image_url: z.any().optional().nullable(),
  lifespan_years: z.preprocess((a) => parseInt(String(a)) || 0, z.number().int().min(0)),
  status: z.string().optional(),
  technical_specs: z.object({
    processor: z.string().optional(),
    memory: z.string().optional(),
    storage: z.string().optional(),
    graphics: z.string().optional(),
    os: z.string().optional(),
    network_card: z.string().optional(),
    ports: z.string().optional(),
  }).optional(),
}).refine(data => {
    if (data.purchase_date && data.warranty_months > 0) {
        const expiryDate = new Date(data.purchase_date);
        expiryDate.setMonth(expiryDate.getMonth() + data.warranty_months);
        return expiryDate > new Date();
    }
    return true;
}, {
    message: "Warranty expiry date must be in the future",
    path: ["warranty_months"],
});

// Draft schema
const draftAssetSchema = z.object({
  model: z.string().min(1, "Asset Name is required for drafts"),
  category: z.string().optional().nullable(),
  serial_number: z.string().optional().nullable(),
  product_name: z.string().optional().nullable(),
  purchase_date: z.string().optional().nullable(),
  purchase_price: z.preprocess((a) => parseFloat(String(a)) || 0, z.number().min(0).optional().nullable()),
  warranty_months: z.preprocess((a) => parseInt(String(a)) || 0, z.number().int().min(0).optional().nullable()),
  supplier_id: z.coerce.string().optional().nullable(),
  location_id: z.string().optional().nullable(),
  image_url: z.any().optional().nullable(),
  lifespan_years: z.preprocess((a) => parseInt(String(a)) || 0, z.number().int().min(0).optional().nullable()),
  status: z.string().optional().nullable(),
});

const AssetRegistration = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const assetTag = searchParams.get('tag');
  const mode = searchParams.get('mode'); // Get mode from URL
  const isEditMode = assetTag !== null;
  const isBulkMode = mode === 'bulk';

  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [imageUrl, setImageUrl] = useState(null);
  const [asset, setAsset] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [selectedDraftId, setSelectedDraftId] = useState(null);

  const { register, handleSubmit, reset, setValue, control, formState: { errors, isSubmitting }, getValues } = useForm({
    resolver: zodResolver(assetRegistrationSchema),
    defaultValues: { 
      category: 'Laptop', 
      warranty_months: 12, 
      lifespan_years: 3, 
      purchase_price: 0,
      location_id: ''
    }
  });

  const { user: authUser } = useSelector((state) => state.auth); // Get user from Redux store
  const userId = authUser?.id;
  const { preferences } = usePreferences();
  const preferredFormat = preferences?.date_format || 'DD/MM/YYYY';

  // --- Fetch Reference Data & Asset Data for Editing ---
  useEffect(() => {
    const fetchReferenceData = async () => {
      // Fetch suppliers
      const { data: supps, error: suppsError } = await supabase.from('suppliers').select('id, company_name');
      if (suppsError) console.error("Error fetching suppliers:", suppsError);

      if (supps) {
        setSuppliers(supps.map(s => ({ value: s.id.toString(), label: s.company_name })));
      }

      // Fetch locations
      const { data: locs, error: locsError } = await supabase.from('locations').select('id, name');
      if (locsError) console.error("Error fetching locations:", locsError);

      if (locs) {
        setLocations(locs.map(l => ({ value: l.id.toString(), label: l.name })));
      }
    };

    const fetchAssetForEdit = async () => {
      if (!isEditMode) return;
      setIsLoading(true);
      try {
        const { data, error } = await supabase.from('assets').select('*').eq('asset_tag', assetTag).single();
        if (error) throw error;
        if (data) {
          // Guard: disposed assets cannot be edited
          const DISPOSED_STATUSES = ['Written Off', 'Lost/Stolen'];
          if (DISPOSED_STATUSES.includes(data.status)) {
            navigate(`/asset-details/${assetTag}`, {
              state: { message: `Cannot edit asset "${data.product_name}" — it has been ${data.status} and is read-only.`, type: 'error' }
            });
            return;
          }
          setAsset(data);
          // Format purchase_date for the date input (YYYY-MM-DD)
          const formattedData = {
            ...data,
            model: data.product_name || '',
            purchase_date: data.purchase_date ? new Date(data.purchase_date).toISOString().split('T')[0] : '',
            supplier_id: String(data.supplier_id),
            location_id: data.location_id ? String(data.location_id) : '',
            lifespan_years: data.lifespan_years || 3,
          };
          reset(formattedData);
          if (data.image_url) {
            setImageUrl(data.image_url);
            setValue('image_url', data.image_url);
          }
        }
      } catch (error) {
        console.error("Error fetching asset for edit:", error);
        addNotification(t('asset_registration.failed_load'), 'error');
      } finally {
        setIsLoading(false);
      }
    };

    if (!isBulkMode) {
      fetchReferenceData();
      fetchAssetForEdit();
      fetchDrafts();
    }
  }, [assetTag, isEditMode, isBulkMode, reset, setValue]);

  const fetchDrafts = async () => {
    const { data, error } = await supabase
      .from('draft_assets')
      .select('*')
      .eq('user_id', userId);
    if (error) {
      console.error('Error fetching drafts:', error);
    } else {
      setDrafts(data);
    }
  };

  const addNotification = (message, type) => {
    setNotifications(prev => [...prev, { id: Date.now(), message, type }]);
  };
  
  const handleImageUpload = (url) => {
    setImageUrl(url);
    setValue('image_url', url);
  };

  const handleSelectDraft = (draft) => {
    setSelectedDraftId(draft.id);
    // Sanitize draft data: convert nulls to empty strings or defaults to prevent Zod validation failures
    const sanitizedDraft = {
      ...draft,
      product_name: draft.product_name || "",
      category: draft.category || "Laptop",
      serial_number: draft.serial_number || "",
      model: draft.product_name || "", // Map database product_name back to form model
      purchase_date: draft.purchase_date || "",
      purchase_price: draft.purchase_price ?? 0,
      warranty_months: draft.warranty_months ?? 0,
      supplier_id: draft.supplier_id ? String(draft.supplier_id) : "",
      location_id: draft.location_id ? String(draft.location_id) : "",
      image_url: draft.image_url || "",
      lifespan_years: draft.lifespan_years ?? 3,
      status: draft.status || "Available",
      technical_specs: draft.technical_specs || {},
    };
    reset(sanitizedDraft);
    setImageUrl(draft.image_url);
  };

  const onInvalid = (errors) => {
    console.error("Validation Errors:", errors);
  };

  const handleDeleteDraft = async (draftId) => {
    const { error } = await supabase.from('draft_assets').delete().eq('id', draftId);
    if (error) {
      addNotification(t('asset_registration.draft_delete_error'), 'error');
    } else {
      addNotification(t('asset_registration.draft_deleted'), 'success');
      setDrafts(drafts.filter(d => d.id !== draftId));
    }
  };

  const handleSaveAsDraft = async () => {
    const formData = getValues();
    const result = draftAssetSchema.safeParse(formData);

    if (!result.success) {
      // Show validation errors for draft
      console.error("Draft validation errors:", result.error);
      addNotification(t('asset_registration.draft_name_required'), "error");
      return;
    }
    
    setIsLoading(true);
    const assetData = {
      product_name: formData.model, // Map form model to database product_name
      category: formData.category,
      serial_number: formData.serial_number,
      purchase_date: formData.purchase_date || null,
      purchase_price: formData.purchase_price ? parseFloat(formData.purchase_price) : null,
      warranty_months: formData.warranty_months ? parseInt(formData.warranty_months) : null,
      supplier_id: formData.supplier_id ? parseInt(formData.supplier_id) : null,
      location_id: formData.location_id ? formData.location_id : null,
      image_url: formData.image_url,
      lifespan_years: formData.lifespan_years ? parseInt(formData.lifespan_years) : null,
      technical_specs: formData.technical_specs || null,
      user_id: userId,
    };

    try {
      if (selectedDraftId) {
        // --- UPDATE DRAFT ---
        const { error } = await supabase.from('draft_assets').update(assetData).eq('id', selectedDraftId);
        if (error) throw error;
        addNotification('Draft updated successfully!', 'success');
        fetchDrafts(); // Refresh drafts list
      } else {
        // --- INSERT DRAFT ---
        const { data, error } = await supabase.from('draft_assets').insert(assetData).select().single();
        if (error) throw error;
        addNotification(`Success! Asset draft saved.`, 'success');
        reset();
        setImageUrl(null);
        fetchDrafts(); // Refresh drafts list
      }
    } catch (error) {
      console.error("Draft Submission Error:", error);
      addNotification(error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // --- Handle Form Submit (Create or Update) ---
  const onSubmit = async (formData) => {
    setIsLoading(true);

    const assetData = {
      product_name: formData.model, // Map form model to database product_name
      category: formData.category,
      serial_number: formData.serial_number,
      purchase_date: formData.purchase_date || null,
      purchase_price: formData.purchase_price ? parseFloat(formData.purchase_price) : null,
      warranty_months: formData.warranty_months ? parseInt(formData.warranty_months) : null,
      supplier_id: formData.supplier_id ? parseInt(formData.supplier_id) : null,
      location_id: formData.location_id ? formData.location_id : null,
      image_url: formData.image_url,
      lifespan_years: formData.lifespan_years ? parseInt(formData.lifespan_years) : null,
      technical_specs: formData.technical_specs || null,
      status: formData.status || 'Available',
    };

    try {
      if (isEditMode) {
        // --- UPDATE ---
        const { error } = await supabase.from('assets').update(assetData).eq('asset_tag', assetTag);
        if (error) throw error;
        // Log activity for asset update
        await logActivity(
          'asset_updated',
          `Updated asset: ${assetData.product_name} (${assetData.serial_number})`,
          assetTag, 
          userId,
          { changes: assetData } // Log the changes made
        );
        navigate('/asset-list', { state: { message: 'Asset details updated successfully!' } });
      } else {
        // --- INSERT ---
        const asset_tag = `ISD-${assetData.category.substring(0,3).toUpperCase()}-${Math.floor(1000 + Math.random() * 9000)}`;
        const { data, error } = await supabase.from('assets').insert({ ...assetData, asset_tag, status: 'Available', registered_by: userId }).select().single();
        if (error) throw error;

        // If this was a draft, delete it
        if (selectedDraftId) {
          await supabase.from('draft_assets').delete().eq('id', selectedDraftId);
        }

        // Log activity for asset added
        await logActivity(
          'asset_added',
          `Added new asset: ${assetData.product_name} (${asset_tag})`,
          data.asset_tag, 
          userId,
          { new_asset_data: assetData }
        );
        addNotification(`Success! Asset ${asset_tag} registered.`, 'success');
        reset();
        setImageUrl(null);
        setSelectedDraftId(null);
        fetchDrafts();
      }
    } catch (error) {
      console.error("Submission Error:", error);
      addNotification(error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  if (isBulkMode) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Bulk Import Assets</h1>
            <p className="text-muted-foreground">Upload a CSV file to add multiple assets at once.</p>
          </div>
        </div>
        <BulkImport />
        <NotificationContainer notifications={notifications} onRemove={(id) => setNotifications(prev => prev.filter(n => n.id !== id))} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{isEditMode ? 'Edit Asset' : 'Register New Asset'}</h1>
          <p className="text-muted-foreground">{isEditMode ? 'Update the details of the existing asset.' : 'Add new equipment to the live database.'}</p>
        </div>
        {!isEditMode && ( // Only show button when not in edit mode
          <Button
            variant="outline"
            iconName="Upload"
            onClick={() => navigate('/asset-registration?mode=bulk')} // Navigate to bulk import mode
          >
            Bulk Import
          </Button>
        )}
      </div>

      <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="space-y-6">
        <AssetDetailsSection 
          register={register} 
          errors={errors} 
          control={control} 
          locations={locations}
        />
        
        <FinancialSection 
          register={register} errors={errors} 
          suppliers={suppliers}
          control={control}
          isEditMode={isEditMode}
          asset={asset}
        />

        <TechnicalSpecsSection 
          register={register} 
          errors={errors} 
        />

        <ImageUpload onUpload={handleImageUpload} initialImageUrl={imageUrl} />

        <div className="flex justify-end gap-3 pt-6 border-t">
            {!isEditMode && <Button type="button" variant="secondary" onClick={handleSaveAsDraft}>{selectedDraftId ? 'Update Draft' : 'Save as Draft'}</Button>}
            <Button type="submit" loading={isSubmitting}>{isEditMode ? 'Save Changes' : 'Register Asset'}</Button>
        </div>
      </form>

      {!isEditMode && !isBulkMode && drafts.length > 0 && (
        <div className="mt-8">
          <h2 className="text-xl font-bold text-foreground mb-4">Your Drafts</h2>
          <div className="space-y-2">
            {drafts.map(draft => (
              <div key={draft.id} className="p-4 bg-muted/50 rounded-lg flex justify-between items-center">
                <div>
                  <p className="font-medium">{draft.product_name}</p>
                  <p className="text-sm text-muted-foreground">
                    Saved on {formatDate(draft.created_at, preferredFormat)}
                  </p>
                </div>
                <div>
                  <Button variant="outline" size="sm" onClick={() => handleSelectDraft(draft)}>
                    Load Draft
                  </Button>
                  <Button variant="destructive" size="sm" className="ml-2" onClick={() => handleDeleteDraft(draft.id)}>
                    Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <NotificationContainer notifications={notifications} onRemove={(id) => setNotifications(prev => prev.filter(n => n.id !== id))} />
    </div>
  );
};


export default AssetRegistration;
